import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  clearItemChangeStatuses,
  markConceptGroupSelectionsAsReported,
} from '@verantos-dev/app-state-cohort-builder';
import {
  selectLatestCohortVersionId,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
  emitSyncConceptGroupSelections,
  selectResearchAssistantModel,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import {
  getCohortBuilderPath,
  getCohortsPath,
  getProjectCohortVersionPath,
  getProjectsBasePath,
} from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import { useProcessCohortMutation } from '@verantos-dev/data-access-cohorts';
import { emitUserSaveEvent } from '@verantos-dev/feature-research-assistant';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortBuilderSaveQuery } from '../../hooks/common/useCohortBuilderSaveQuery';
import { getCohortEditModalTitle } from '../../utils/getCohortEditModalTitle';

export const useProcessCohortDefinitionModal = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const zustandLatestStatus = useCohortDefinitionStore(selectLatestStatus);
  // TODO: cleanup with research_assistant_gen_3 ff — once gen_3 is the
  // default, drop the legacy `getCohortBuilderPath` branch below.
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false) as boolean;

  const latestCohortVersionId = useCohortDefinitionStore(selectLatestCohortVersionId);
  const { closeDialog } = useCohortBuilderModalContext();
  const researchAssistantModel = useAppSelector(selectResearchAssistantModel);
  const { user } = useAuth();

  const {
    mutateAsync: postCohortExecution,
    isPending: isPostCohortExecutionLoading,
    error: postCohortExecutionError,
  } = useProcessCohortMutation();

  const {
    projectId,
    cohortId,
    cohortData,
    submitVersion,
    isInitialLoading,
    initialLoadingError,
    translationFailed,
    isSubmitting,
    submittingError,
    isSubmitSuccess,
  } = useCohortBuilderSaveQuery(postCohortExecution);

  const effectiveInitialLoadingError = translationFailed
    ? {
        name: 'Translation Error',
        message: intl.formatMessage({
          id: 'projects.cohortBuilderPage.modal.translationFailed',
        }),
      }
    : initialLoadingError;

  const cohortBuilderPath = projectId
    ? cohortId && latestCohortVersionId
      ? `${
          isGen3Enabled
            ? getProjectCohortVersionPath(projectId, cohortId, latestCohortVersionId)
            : getCohortBuilderPath(projectId, cohortId, latestCohortVersionId)
        }?pageTab=${CohortBuilderPageTab.results}` // route to cohort detail view (gen_3) or legacy cohort builder
      : getCohortsPath(projectId) // route to cohorts list if no cohort id
    : getProjectsBasePath(); // route to projects list if no project id

  const onClose = () => {
    if (!isPostCohortExecutionLoading) {
      closeDialog();

      isSubmitSuccess && navigate(cohortBuilderPath);
    }
  };

  const onRedirectAfterSubmit = () => {
    closeDialog();
    navigate(cohortBuilderPath);
  };

  const onSubmit = () => {
    // Clear NEW/UPDATED indicators on process
    useCohortDefinitionStore.getState().clearChangeStatuses();
    dispatch(clearItemChangeStatuses());

    // Sync all concept group selections before processing.
    // Per AC, the process action clears BOTH warning and error chips:
    //   - Warning chip clears when cohort processing is triggered.
    //   - Error chip clears on a cohort-routed message (process is one).
    // The slice's existing markAll carve-out (skip error selections with no
    // selectedConceptGroups) keeps genuinely-blocking errors visible.
    if (cohortId) {
      dispatch(markConceptGroupSelectionsAsReported({ markAll: true }));
      useCohortDefinitionStore.getState().markConceptGroupSelectionsAsReported({ markAll: true });
      emitSyncConceptGroupSelections({ cohortId, markAll: true });
    }

    submitVersion();
    if (projectId && cohortId && latestCohortVersionId) {
      emitUserSaveEvent({
        model: researchAssistantModel,
        cohortVersionId: latestCohortVersionId,
        cohortId,
        projectId,
        user,
        cohortAction: COHORT_ACTIONS.PROCESS,
      });
    }
    dispatch(
      addResearchAssistantMessages([
        {
          message: intl.formatMessage({
            id: 'projects.cohortBuilderPage.researchAssistant.processedInfoMessage',
          }),
          timestamp: new Date().toISOString(),
          currentUser: 'user',
          isCurrentUser: true,
          type: ResearchAssistantMessageEnumLegacy.PROCESS,
        },
      ])
    );
  };

  return {
    title: getCohortEditModalTitle(
      intl,
      CohortBuilderActionDialogType.PROCESS_COHORT_DEFINITION,
      true
    ),
    onClose,
    onSubmit,
    onRedirectAfterSubmit,
    cohortName: cohortData?.cohortName,
    isSubmitting,
    submittingError,
    isPostCohortExecutionLoading,
    initialLoadingError: effectiveInitialLoadingError,
    latestStatus: zustandLatestStatus,
    postCohortExecutionError,
    isInitialLoading,
  };
};
